import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, CheckCircle2, DollarSign, Calendar, MapPin, Store, Search, Star, EyeOff, Eye, Trash2, AlertTriangle } from "lucide-react";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1600334129128-685c5582fd35?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const benefits = [
  { icon: DollarSign, title: "70/30 Revenue Split", text: "Keep 70% of what you offer at ecosystem locations — or 90% at your own space with a 10% finder's fee." },
  { icon: Calendar, title: "Bookings & Scheduling", text: "Set your services, pricing, availability, and let members book you directly." },
  { icon: MapPin, title: "Your Own Locations", text: "Host at ecosystem spaces, your own studio, partner venues, or online." },
  { icon: Store, title: "Sell Products", text: "Offer digital and physical products through the marketplace." },
];

function EnhancerDirectory() {
  const [q, setQ] = useState("");
  const [focus, setFocus] = useState("all");
  const [modality, setModality] = useState("all");
  const [bookableOnly, setBookableOnly] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const qc = useQueryClient();
  const { data: enhancers = [], isLoading } = useQuery({
    queryKey: ["public-enhancers", q],
    queryFn: () => api.get(`/enhancers?q=${encodeURIComponent(q)}`).then((r) => r.data),
    refetchInterval: 60000,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["public-enhancers"] });
  const toggleHide = async (e) => {
    try {
      await api.put(`/admin/users/${e.id}`, { show_in_directory: !!e.hidden });
      toast.success(e.hidden ? `${e.name} is now visible in the directory.` : `${e.name} is now hidden from the directory.`);
      refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't update."); }
  };
  const removeEnhancer = async (e) => {
    if (!window.confirm(`Delete ${e.name}'s account entirely? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${e.id}`);
      toast.success(`${e.name} deleted.`);
      refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't delete."); }
  };
  const focusAreas = useMemo(() => {
    const s = new Set();
    enhancers.forEach((e) => (e.focus_areas || []).forEach((f) => s.add(f)));
    return Array.from(s).sort();
  }, [enhancers]);
  const modalities = useMemo(() => {
    const s = new Set();
    enhancers.forEach((e) => (e.modalities || []).forEach((m) => s.add(m)));
    return Array.from(s).sort();
  }, [enhancers]);
  let shown = focus === "all" ? enhancers : enhancers.filter((e) => (e.focus_areas || []).includes(focus));
  if (modality !== "all") shown = shown.filter((e) => (e.modalities || []).includes(modality));
  if (bookableOnly) shown = shown.filter((e) => (e.service_count || 0) > 0 && e.accepting_new !== false);
  return (
    <div className="mb-20" data-testid="enhancer-directory">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <p className="eyw-label mb-3">Meet our Enhancers</p>
          <h2 className="font-serif text-3xl lg:text-4xl text-[#1A242B]">Find a practitioner.</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {modalities.length > 0 && (
            <select value={modality} onChange={(e) => setModality(e.target.value)} data-testid="enhancer-modality-filter"
              className="eyw-input text-sm !py-2.5 pr-8 cursor-pointer">
              <option value="all">All session types</option>
              {modalities.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          )}
          <div className="flex items-center gap-2 eyw-input max-w-xs w-full">
            <Search size={15} className="text-[#8FA89B]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, modality, need, city" data-testid="enhancer-search" className="bg-transparent outline-none flex-1 text-sm" />
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-8" data-testid="enhancer-focus-filters">
        <button onClick={() => setBookableOnly((v) => !v)} data-testid="enhancer-bookable-filter" className={`px-3.5 py-1.5 rounded-full text-sm transition-colors inline-flex items-center gap-1.5 ${bookableOnly ? "bg-[#5C8A72] text-white" : "bg-white border border-[#5C8A72]/40 text-[#5C8A72] hover:bg-[#5C8A72]/[0.06]"}`}>
          <Calendar size={13} /> Bookable now
        </button>
        {focusAreas.length > 0 && (
          <>
            <span className="w-px bg-[#E5E0D8] mx-1 self-stretch" />
            <button onClick={() => setFocus("all")} data-testid="enhancer-focus-all" className={`px-3.5 py-1.5 rounded-full text-sm transition-colors ${focus === "all" ? "bg-[#144F75] text-white" : "bg-white border border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]"}`}>All areas of support</button>
            {focusAreas.map((f) => (
              <button key={f} onClick={() => setFocus(f)} data-testid={`enhancer-focus-${f.toLowerCase().replace(/\s+/g, "-")}`} className={`px-3.5 py-1.5 rounded-full text-sm transition-colors ${focus === f ? "bg-[#144F75] text-white" : "bg-white border border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]"}`}>{f}</button>
            ))}
          </>
        )}
      </div>
      {isLoading ? (
        <div className="grid place-items-center py-16 text-[#4A5D6B]"><Loader2 className="animate-spin" /></div>
      ) : shown.length === 0 ? (
        <p className="text-[#4A5D6B] py-8" data-testid="enhancer-empty">{bookableOnly ? "No Enhancers are open for booking in this view yet. Clear the “Bookable now” filter to see everyone, or call 859-979-5505 and we'll help." : "No Enhancers match your search yet. Try a different area of support, or call 859-979-5505 and we'll help."}</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="enhancer-directory-grid">
          {shown.map((e) => {
            const bookable = (e.service_count || 0) > 0 && e.accepting_new !== false;
            const first = (e.name || "").split(" ")[0];
            return (
              <div key={e.id} data-testid={`enhancer-dir-${e.id}`} className={`eyw-card p-6 flex flex-col hover:border-[#144F75] transition-colors ${isAdmin && (e.hidden || (e.incomplete || []).length) ? "border-[#E0A458]/60 bg-[#E0A458]/[0.04]" : ""}`}>
                {isAdmin && (e.hidden || (e.incomplete || []).length > 0) && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {e.hidden && <span data-testid={`admin-hidden-${e.id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase rounded-full bg-[#4A5D6B]/10 text-[#4A5D6B] px-2 py-0.5"><EyeOff size={10} /> Hidden</span>}
                    {(e.incomplete || []).length > 0 && (
                      <span data-testid={`admin-incomplete-${e.id}`} title={`Missing: ${e.incomplete.join(", ")}`} className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase rounded-full bg-red-100 text-red-600 px-2 py-0.5">
                        <AlertTriangle size={10} /> Incomplete: {e.incomplete.join(", ")}
                      </span>
                    )}
                  </div>
                )}
                <Link to={`/enhancers/${e.slug || e.id}`} className="flex gap-4 items-start flex-1">
                  {e.avatar
                    ? <img src={e.avatar} alt={e.name} className="h-16 w-16 rounded-full object-cover shrink-0" />
                    : <span className="grid place-items-center h-16 w-16 rounded-full bg-[#144F75]/10 text-[#144F75] text-2xl font-semibold shrink-0">{(e.name || "?").slice(0, 1)}</span>}
                  <div className="min-w-0">
                    <p className="font-semibold text-[#1A242B] truncate">{e.name}</p>
                    {e.headline && <p className="text-sm text-[#144F75] truncate">{e.headline}</p>}
                    <div className="flex items-center gap-1 mt-1 text-sm text-[#4A5D6B]">
                      <Star size={13} className={e.avg_rating ? "fill-[#E0A458] text-[#E0A458]" : "text-[#D8D2C8]"} />
                      {e.avg_rating || "New"} {e.review_count > 0 && `(${e.review_count})`} · {e.service_count} {e.service_count === 1 ? "service" : "services"}
                    </div>
                    {e.location && <p className="text-xs text-[#8FA89B] flex items-center gap-1 mt-1"><MapPin size={11} /> {e.location}</p>}
                    {(e.focus_areas || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {e.focus_areas.slice(0, 3).map((f) => <span key={f} className="text-[10px] rounded-full bg-[#144F75]/8 text-[#144F75] px-1.5 py-0.5">{f}</span>)}
                        {e.focus_areas.length > 3 && <span className="text-[10px] text-[#8FA89B]">+{e.focus_areas.length - 3}</span>}
                      </div>
                    )}
                  </div>
                </Link>
                <div className="mt-4 pt-4 border-t border-[#E5E0D8] flex gap-2">
                  <Link to={`/enhancers/${e.slug || e.id}`} data-testid={`enhancer-view-${e.id}`} className="eyw-btn-ghost !py-2 text-sm flex-1 justify-center">View profile</Link>
                  {bookable
                    ? <Link to={`/enhancers/${e.slug || e.id}`} data-testid={`enhancer-book-${e.id}`} className="eyw-btn !py-2 text-sm flex-1 justify-center"><Calendar size={15} /> Book with {first}</Link>
                    : <span data-testid={`enhancer-comingsoon-${e.id}`} className="flex-1 text-center text-xs text-[#8FA89B] self-center">Booking soon</span>}
                </div>
                {isAdmin && (
                  <div className="mt-2 pt-2 border-t border-dashed border-[#E5E0D8] flex gap-2">
                    <button onClick={() => toggleHide(e)} data-testid={`admin-toggle-hide-${e.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-medium text-[#4A5D6B] hover:text-[#144F75] py-1">
                      {e.hidden ? <><Eye size={13} /> Show in directory</> : <><EyeOff size={13} /> Hide from directory</>}
                    </button>
                    <button onClick={() => removeEnhancer(e)} data-testid={`admin-delete-${e.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-medium text-red-500 hover:text-red-600 py-1">
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Enhancers() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", profession: "", modalities: "",
    experience: "", website: "", location_city: "", location_state: "", message: "",
    business_name: "", professional_title: "", credentials: "", address: "", zip: "", service_areas: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/enhancer/apply", form);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="enhancers-page">
      <Seo
        title="Find a Wellness Enhancer"
        description="Browse certified wellness practitioners — Reiki, massage, breathwork, sound healing, coaching, and more in Newport and Verona, KY. Book a session online."
        image={HERO}
        path="/enhancers"
      />
      <PageHero
        label="Enhancer Directory"
        title="Meet the Enhancers who help you grow."
        subtitle="Explore our network of practitioners, teachers, facilitators, artists, and musicians — people who help you enhance your professional, personal, relational, creative, physical, emotional, and spiritual experiences. Find the right guide, then book with ease."
        image={HERO}
        testid="enhancers-hero"
      />
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <EnhancerDirectory />

        <div className="rounded-2xl border border-[#E5E0D8] bg-[#F9F7F3] p-6 sm:p-8 mb-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="become-partner-banner">
          <div>
            <p className="eyw-label mb-1">Grow with us</p>
            <h3 className="font-serif text-2xl text-[#1A242B]">Are you a practitioner, teacher, or space?</h3>
            <p className="text-sm text-[#4A5D6B] mt-1">Join the network — exposure, ease, flow, and the tools to thrive.</p>
          </div>
          <Link to="/partner" data-testid="enhancers-become-partner" className="eyw-btn shrink-0 !py-2.5 text-sm">Become a Partner</Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {benefits.map((b) => (
            <div key={b.title} className="eyw-card p-7">
              <span className="grid place-items-center h-12 w-12 rounded-full bg-[#144F75]/10 text-[#144F75] mb-4"><b.icon size={22} /></span>
              <h3 className="text-lg text-[#1A242B] mb-1">{b.title}</h3>
              <p className="text-[#4A5D6B] text-sm leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 items-start">
          <div>
            <p className="eyw-label mb-3">The Enhancer Path</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-[#1A242B] leading-tight">You're responsible for your participation. We provide the container.</h2>
            <p className="text-[#4A5D6B] mt-5 leading-relaxed">
              You create and edit your profile, set your services and pricing, manage availability, and communicate
              with participants. The ecosystem provides technology, standards, agreements, visibility, payment flow,
              and support — so you can focus on showing up and offering your gifts with integrity.
            </p>
            <p className="text-[#4A5D6B] mt-4 leading-relaxed">
              Applications are reviewed by admin. Once approved, your Enhancer dashboard unlocks service creation,
              scheduling, locations, revenue tracking, and payouts.
            </p>
          </div>

          <div className="eyw-card p-8" id="apply">
            {done ? (
              <div className="text-center py-10" data-testid="enhancer-apply-success">
                <CheckCircle2 size={48} className="text-[#144F75] mx-auto mb-4" />
                <h3 className="font-serif text-2xl text-[#1A242B]">Application received.</h3>
                <p className="text-[#4A5D6B] mt-2">Thank you. Our team will review your application and reach out soon.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="enhancer-apply-form">
                <h3 className="font-serif text-2xl text-[#1A242B] mb-2">Apply to become an Enhancer</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required placeholder="Full name" value={form.name} onChange={set("name")} data-testid="enhancer-name" className="eyw-input" />
                  <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="enhancer-email" className="eyw-input" />
                  <input placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="enhancer-phone" className="eyw-input" />
                  <input placeholder="Profession (e.g. Reiki, teacher, musician)" value={form.profession} onChange={set("profession")} data-testid="enhancer-profession" className="eyw-input" />
                  <input placeholder="City" value={form.location_city} onChange={set("location_city")} data-testid="enhancer-city" className="eyw-input" />
                  <input placeholder="State" value={form.location_state} onChange={set("location_state")} data-testid="enhancer-state" className="eyw-input" />
                </div>
                <input placeholder="Modalities / offerings" value={form.modalities} onChange={set("modalities")} data-testid="enhancer-modalities" className="eyw-input w-full" />
                <input placeholder="Website / social (optional)" value={form.website} onChange={set("website")} data-testid="enhancer-website" className="eyw-input w-full" />
                <p className="text-xs tracking-[0.15em] uppercase text-[#8FA89B] pt-2">Business details (optional)</p>
                <div className="grid grid-cols-2 gap-3">
                  <input placeholder="Business name" value={form.business_name} onChange={set("business_name")} data-testid="enhancer-business-name" className="eyw-input" />
                  <input placeholder="Professional title" value={form.professional_title} onChange={set("professional_title")} data-testid="enhancer-title" className="eyw-input" />
                  <input placeholder="Credentials / licenses" value={form.credentials} onChange={set("credentials")} data-testid="enhancer-credentials" className="eyw-input" />
                  <input placeholder="Service areas" value={form.service_areas} onChange={set("service_areas")} data-testid="enhancer-service-areas" className="eyw-input" />
                  <input placeholder="Address" value={form.address} onChange={set("address")} data-testid="enhancer-address" className="eyw-input" />
                  <input placeholder="ZIP / Postal code" value={form.zip} onChange={set("zip")} data-testid="enhancer-zip" className="eyw-input" />
                </div>
                <textarea placeholder="Tell us about your experience and what you'd like to offer" rows={4} value={form.message} onChange={set("message")} data-testid="enhancer-message" className="eyw-input w-full resize-none" />
                {error && <p className="text-sm text-red-600" data-testid="enhancer-error">{error}</p>}
                <button type="submit" disabled={loading} data-testid="enhancer-submit" className="eyw-btn w-full disabled:opacity-60">
                  {loading && <Loader2 className="animate-spin" size={18} />} Submit Application
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
